import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { COUNTING_ENGINE_THEME, countingSpokenWord, countingTagPiece, countingTarget } from "../../../../data/countingLadderCatalog";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  BigCounter, CornerPill, CountBadge, CountingPanel, CountingPiece, CountingRoot, PanelChip, PieceField, POP_SPRING, ShakeWrap,
  pieceScale, useCompleteOnce, useFitPieceSize, useWrongFlash,
} from "../CountingPiece";

/** Deterministic scatter spots (percent of the field) for the "off the line" rounds. */
const SCATTER = [
  { x: 18, y: 24, r: -8 }, { x: 52, y: 16, r: 6 }, { x: 82, y: 28, r: -4 }, { x: 30, y: 58, r: 10 },
  { x: 64, y: 50, r: -6 }, { x: 86, y: 74, r: 5 }, { x: 16, y: 80, r: -10 }, { x: 48, y: 84, r: 7 },
];

/** Scattered pieces: set-scaled, but never so big two spots could touch. */
const scatterSize = (count) =>
  `max(56px, min(calc(var(--tile) * ${pieceScale(count)}), calc(var(--arena-h, 640px) * 0.24), calc(var(--arena-w, 760px) * 0.2)))`;

function layoutFor(activity) {
  if (activity.number === 18 || activity.number === 44) return "scatter";
  return "grid";
}

/**
 * One-to-one tagging: every piece counts exactly once, and each tap speaks
 * the next number word. Re-tapping a counted piece is the mistake this
 * engine teaches against, so it shakes and reports wrong.
 */
const TagCountGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion = false }) => {
  const target = countingTarget(activity);
  const accent = COUNTING_ENGINE_THEME.tagCount.accent;
  const layout = layoutFor(activity);
  const [tagged, setTagged] = useState([]);
  const [burst, setBurst] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const { flash, isWrong } = useWrongFlash();
  const done = tagged.length === target;

  const items = useMemo(
    () => Array.from({ length: target }, (_, i) => ({ id: i, asset: countingTagPiece(activity, i), spot: SCATTER[i % SCATTER.length] })),
    [activity, target],
  );
  const fit = useFitPieceSize({ count: target });
  const size = layout === "scatter" ? scatterSize(target) : fit.size;

  const tag = (i) => {
    if (done) return;
    if (tagged.includes(i)) {
      flash(i);
      onWrong?.();
      return;
    }
    const next = [...tagged, i];
    setTagged(next);
    const n = next.length;
    speak?.(countingSpokenWord(n), { interrupt: true });
    if (n === target) {
      sfx?.("correct");
      setBurst((b) => b + 1);
      complete();
    } else {
      sfx?.("pop", { rate: 1 + n * 0.04 });
    }
  };

  const renderItem = (item, extraStyle) => {
    const position = tagged.indexOf(item.id);
    const counted = position >= 0;
    return (
      <div key={item.id} style={{ display: "inline-flex", ...extraStyle }}>
        <ShakeWrap shake={isWrong(item.id)} reduceMotion={reduceMotion} style={{ display: "inline-flex" }}>
          <motion.button
            type="button"
            className="counting-btn"
            aria-label={counted ? "Counted item" : "Uncounted item"}
            onClick={() => tag(item.id)}
            initial={reduceMotion ? false : { scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1, rotate: layout === "scatter" ? item.spot.r : 0, y: counted ? -8 : 0 }}
            transition={{ ...POP_SPRING, delay: reduceMotion || counted ? 0 : Math.min(item.id * 0.06, 0.7) }}
            whileTap={reduceMotion || counted ? undefined : { scale: 0.92 }}
            whileHover={reduceMotion || counted ? undefined : { scale: 1.06 }}
            style={{ background: "none", border: "none", padding: 0, cursor: counted ? "default" : "pointer", position: "relative", display: "inline-flex", borderRadius: 24, touchAction: "manipulation" }}
          >
            <CountingPiece asset={item.asset} size={size} variant="sticker" phase={item.id} floats={!counted} lifted={counted} reduceMotion={reduceMotion} ring={counted ? accent : undefined} />
            {counted && <CountBadge n={position + 1} accent={accent} reduceMotion={reduceMotion} />}
          </motion.button>
        </ShakeWrap>
      </div>
    );
  };

  return (
    <CountingRoot>
      <CountingPanel
        grow
        tint={accent}
        chip={<PanelChip text={done ? "All counted" : "Tap each one"} accent={accent} />}
        corner={<CornerPill>{tagged.length} of {target}</CornerPill>}
        style={layout === "scatter" ? { padding: "calc(var(--tile) * 0.72) 0 calc(var(--tile) * 0.3)" } : undefined}
      >
        {layout === "scatter" ? (
          <div style={{ position: "relative", flex: "1 1 0%", width: "100%", minHeight: "calc(var(--tile) * 3.4)" }}>
            {items.map((item) => renderItem(item, {
              position: "absolute",
              left: `clamp(calc(${size} / 2 + 6px), ${item.spot.x}%, calc(100% - ${size} / 2 - 6px))`,
              top: `clamp(calc(${size} / 2 + 6px), ${item.spot.y}%, calc(100% - ${size} / 2 - 6px))`,
              transform: "translate(-50%, -50%)",
            }))}
          </div>
        ) : (
          <PieceField fieldRef={fit.ref} size={fit.size} gap={fit.gap} cols={fit.cols}>
            {items.map((item) => renderItem(item))}
          </PieceField>
        )}
        {burst > 0 && <ConfettiBurst key={`burst-${burst}`} count={28} originY={45} reduceMotion={reduceMotion} />}
      </CountingPanel>
      <BigCounter
        value={tagged.length}
        total={target}
        word={done ? `${countingSpokenWord(target)} altogether` : undefined}
        accent={accent}
        placeholder="Ready to count"
      />
    </CountingRoot>
  );
};

export default TagCountGame;
